import { Component, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserFacade } from '../store/user/facade/user.facade';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PasswordResetSuccessModalComponent } from './password-reset-success-modal/password-reset-success-modal.component';
import { passwordValidator } from '../shared/validators/password.validator';
import { EMAIL_TLD_PATTERN } from '../shared/validators/email.validator';
import { UserService } from '../services/user.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-password-help',
  templateUrl: './password-help.component.html',
  styleUrls: ['./password-help.component.scss'],
})
export class PasswordHelpComponent implements OnDestroy {
  authError$ = this.userFacade.authError$;
  pwdResetCodeSent: boolean = true;
  pwdUpdateSubmitted: boolean = false;
  passwordHelpForm: FormGroup;
  title: string = 'Need password help?';
  subTitle: string =
    'Type in the email associated with your account and we’ll send you an email to reset it.';
  submitted: boolean = false;
  error: string;
  isValidEmail = false;
  isLoading = false;
  errorMsg: string = '';
  constructor(
    private router: Router,
    private userFacade: UserFacade,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private userservice: UserService,
    private toastr: ToastrService,
  ) {
    this.passwordHelpForm = this.formBuilder.group({
      email: new FormControl(null, [
        Validators.required,
        Validators.email,
        Validators.pattern(EMAIL_TLD_PATTERN),
      ]),
      code: new FormControl(null, [Validators.required]),
      password: [null, [Validators.required, passwordValidator()]],
      confirmPassword: [null, [Validators.required]],
    }, { validator: this.passwordMatchValidator });
  }

  passwordMatchValidator(group: FormGroup) {
    const passwordControl = group.get('password');
    const confirmPasswordControl = group.get('confirmPassword');

    if (confirmPasswordControl.errors && !confirmPasswordControl.errors['passwordsMismatch']) {
      // Return if another validator has already found an error on the confirmPasswordControl
      return;
    }

    if (passwordControl.value !== confirmPasswordControl.value) {
      confirmPasswordControl.setErrors({ passwordsMismatch: true });
    } else {
      confirmPasswordControl.setErrors(null);
    }
  }

  ngOnInit() {
    this.pwdResetCodeSent = false;
    this.userFacade.pwdResetCodeSent$.subscribe((x) => {
      this.pwdResetCodeSent = x;
      this.isLoading = false;
      if (this.pwdResetCodeSent) {
        this.passwordHelpForm.get('code').setValidators(Validators.required);
        this.passwordHelpForm.get('password').setValidators([Validators.required, passwordValidator()]);
        this.passwordHelpForm.get('confirmPassword').setValidators(Validators.required);

        this.passwordHelpForm.get('email').clearValidators();
        this.passwordHelpForm.get('code').updateValueAndValidity();
        this.passwordHelpForm.get('email').updateValueAndValidity();
        this.passwordHelpForm.get('password').updateValueAndValidity();
        this.passwordHelpForm.get('confirmPassword').updateValueAndValidity();
      }
    });

    this.userFacade.authError$.subscribe(error => {
      if (error) {
        console.log('Auth Error:', error);
        this.isLoading = false;
      }
    });

    this.route.queryParams.subscribe(params => {
      const email = params['email'];
      console.log(email);

      if (email) {
        this.passwordHelpForm.get('email')?.setValue(email);
        this.pwdResetCodeSent = false;
        this.onSubmit(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.userFacade.resetErrorMsg();
  }

  navigateToSignIn(): void {
    this.router.navigate(['/signin']);
  }

  get f(): { [key: string]: AbstractControl } {
    return this.passwordHelpForm.controls;
  }

  onSubmit(isHome: boolean) {
    if (isHome == false) {
      this.router.navigate(['/signin']);
    } else {
      !this.pwdResetCodeSent
        ? (this.submitted = true)
        : (this.pwdUpdateSubmitted = true);

      if (!this.pwdResetCodeSent) {
        if (this.hasEmailError()) {
          this.isValidEmail = false;
          return;
        }
        if (this.isLoading) return;
        this.isLoading = true;
        this.isValidEmail = true;
        this.userservice.sendPwdResetCode(this.passwordHelpForm.value.email).subscribe({
          next: (result) => {
            console.log(result);
            this.pwdResetCodeSent = true;
            this.isLoading = false;
            this.toastr.success('Check your email for a temporary password to reset it.', '', {
              positionClass: 'toast-top-center',
              closeButton: false,
              timeOut: 4500,
            });
          },
          error: (error) => {
            this.errorMsg = this.extractApiErrorMessage(error);
            this.isLoading = false;
            this.isValidEmail = false;
          }
        });
      } else {
        this.trimCodeField();
        console.log(this.passwordHelpForm.get('password').errors);
        if (!this.passwordHelpForm.valid) {
          console.log("jjj")

          return;
        }

        let formValue = this.passwordHelpForm.value;

        if (formValue.password != formValue.confirmPassword) {
          this.error = 'Passwords are not equal';
          this.isLoading = false;
          return;
        }
        if (this.isLoading) return;
        console.log("resetPassword")
        this.isLoading = true;
        this.isValidEmail = true;
        // this.userFacade.resetPassword(this.passwordHelpForm.value);
        this.userservice.resetPassword(this.passwordHelpForm.value)
          .subscribe({
            next: (result) => {
              console.log(result);
              this.isLoading = false;
              const modalRef = this.modalService.open(
                PasswordResetSuccessModalComponent,
              );
              modalRef.componentInstance.modalTitle = 'Success';
              modalRef.componentInstance.modalBodyText =
                'Your password has been reset';
              modalRef.componentInstance.moveToSignin = true;
            },
            error: (error) => {
              this.errorMsg = this.extractApiErrorMessage(error);
              this.isLoading = false;
              this.passwordHelpForm.get('code').setErrors({ apiError: true });
            }
          });
      }
    }


  }

  /**
   * Strips stray whitespace off the pasted temporary password so the field looks
   * tidy and matches what gets sent. Cosmetic only: the backend already trims
   * (#88), so a code that worked before still works and one that failed still
   * fails the same way.
   *
   * CODE FIELD ONLY, deliberately — a real password may legitimately contain
   * leading or trailing spaces, and trimming those would silently corrupt it.
   *
   * Runs on submit rather than on blur. Blur would be wrong here: setValue()
   * re-runs the control's validators and discards the apiError set by
   * resetPassword()'s error handler, so merely focusing and leaving a rejected
   * field would clear the red state and re-enable Update for the SAME known-bad
   * code, undoing #5. On submit the ordering is safe — trim, then send, then
   * flag on failure — and the guard means an unchanged value never touches form
   * state at all.
   *
   * Note: a whitespace-only entry now trims to '' and fails the required
   * validator client-side instead of round-tripping to a server rejection. Same
   * outcome, fewer steps.
   */
  private trimCodeField(): void {
    const codeControl = this.passwordHelpForm.get('code');
    const value = codeControl?.value;

    if (typeof value !== 'string') {
      return;
    }

    const trimmed = value.trim();
    if (trimmed !== value) {
      codeControl.setValue(trimmed);
    }
  }

  /**
   * UserService.resetPassword() asks for responseType:'text' — added deliberately
   * in f22f510, because this endpoint's success body isn't JSON and letting
   * Angular parse it routed a successful reset into the error callback. The cost
   * is that HttpErrorResponse.error arrives as a raw STRING, so reading
   * error.error.error.message was always undefined and EVERY failure surfaced as
   * "Unknown error occurred" — including "Code is invalid or expired".
   *
   * The API also uses two body shapes, both seen live on crud-develop: flat
   * {"message":"..."} from the Cognito path and nested
   * {"success":false,"error":{"message":"..."}} from request validation.
   *
   * The generic is a genuine last resort now: it shows only when the body isn't
   * parsable JSON (gateway HTML, empty response), where there is nothing safe to
   * display, rather than on every error.
   */
  private extractApiErrorMessage(error: HttpErrorResponse): string {
    let body = error?.error;

    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return 'Unknown error occurred';
      }
    }

    return body?.error?.message || body?.message || 'Unknown error occurred';
  }

  hasEmailError(): boolean {
    return !!this.passwordHelpForm.get('email')?.invalid;
  }

  clearInput(input: string): void {
    console.log("ws");
    this.passwordHelpForm.controls['email'].setValue('');
    this.submitted = false;
  }

  open() {
    const modalRef = this.modalService.open(PasswordResetSuccessModalComponent);
    modalRef.componentInstance.modalTitle = 'Success';
    modalRef.componentInstance.modalBodyText = 'Your password has been reset';
    modalRef.componentInstance.moveToSignin = true;
  }

  ngDestroy() {
    this.pwdResetCodeSent = false;
    this.isLoading = false;
  }
}
